import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "reactstrap";
import { useIntl } from "react-intl";
import { WEMBII_AI_CREDITS_TOGGLE_ATTR } from "../../constants/dom.constant";
import "./LowCreditsPromptOverlay.scss";

type Props = {
  isOpen: boolean;
  dontShowAgain: boolean;
  credits: number;
  onDontShowAgainChange: (value: boolean) => void;
  onLater: () => void;
  onBuyNow: () => void;
};

function getVisibleCreditsToggleEl(): HTMLElement | null {
  const selector = `[${WEMBII_AI_CREDITS_TOGGLE_ATTR}]`;
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(selector));
  return nodes.find((el) => el.offsetParent !== null) ?? null;
}

export function LowCreditsPromptOverlay({
  isOpen,
  dontShowAgain,
  credits,
  onDontShowAgainChange,
  onLater,
  onBuyNow,
}: Props) {
  const intl = useIntl();
  const maskId = useRef(
    `low-credits-prompt-mask-${Math.random().toString(36).slice(2, 11)}`
  );
  const cardRef = useRef<HTMLDivElement>(null);

  const [hole, setHole] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [cardStyle, setCardStyle] = useState<React.CSSProperties>({});

  const measureHole = useCallback(() => {
    const el = getVisibleCreditsToggleEl();
    if (!el) {
      setHole(null);
      return;
    }
    const pad = 10;
    const r = el.getBoundingClientRect();
    setHole({
      left: r.left - pad,
      top: r.top - pad,
      width: r.width + pad * 2,
      height: r.height + pad * 2,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setHole(null);
      setCardStyle({});
      return;
    }
    measureHole();
    const t1 = window.setTimeout(measureHole, 50);
    const t2 = window.setTimeout(measureHole, 200);
    const onWin = () => measureHole();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [isOpen, measureHole]);

  const layoutCard = useCallback(() => {
    if (!isOpen) return;

    if (!hole) {
      setCardStyle({});
      return;
    }

    const cardW = Math.min(340, window.innerWidth - 32);
    const left = Math.max(
      16,
      Math.min(
        hole.left + hole.width / 2 - cardW / 2,
        window.innerWidth - cardW - 16
      )
    );

    const gap = 12;
    const bottomEdge = hole.top + hole.height;
    const cardH = cardRef.current?.offsetHeight ?? 220;
    const spaceBelow = window.innerHeight - bottomEdge - gap;

    if (spaceBelow >= cardH + 16) {
      setCardStyle({
        left,
        top: bottomEdge + gap,
        transform: "none",
      });
    } else {
      setCardStyle({
        left,
        top: hole.top - gap,
        transform: "translateY(-100%)",
      });
    }
  }, [isOpen, hole]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    layoutCard();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      layoutCard();
      raf2 = requestAnimationFrame(() => {
        layoutCard();
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isOpen, hole, dontShowAgain, layoutCard]);

  if (!isOpen || typeof document === "undefined") return null;

  const w = window.innerWidth;
  const h = window.innerHeight;
  const showSpotlight = Boolean(hole);
  const centerCard = !hole;

  const node = (
    <div className="low-credits-prompt-overlay" role="dialog" aria-modal="true">
      {!showSpotlight && (
        <button
          type="button"
          className="low-credits-prompt-overlay__dim-full"
          aria-hidden="true"
          tabIndex={-1}
          onClick={onLater}
        />
      )}

      {showSpotlight && hole && (
        <>
          <svg
            className="low-credits-prompt-overlay__svg"
            width={w}
            height={h}
            aria-hidden="true"
          >
            <defs>
              <mask id={maskId.current}>
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={hole.left}
                  y={hole.top}
                  width={hole.width}
                  height={hole.height}
                  rx={14}
                  ry={14}
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="rgba(15, 23, 42, 0.62)"
              mask={`url(#${maskId.current})`}
              className="low-credits-prompt-overlay__dim"
              onClick={onLater}
            />
          </svg>
          <div
            className="low-credits-prompt-overlay__zoom"
            style={{
              left: hole.left,
              top: hole.top,
              width: hole.width,
              height: hole.height,
            }}
            aria-hidden="true"
          />
        </>
      )}

      <div
        ref={cardRef}
        className={`low-credits-prompt-overlay__card ${
          centerCard ? "low-credits-prompt-overlay__card--center" : ""
        }`}
        style={cardStyle}
      >
        <h2 className="low-credits-prompt-overlay__title">
          {intl.formatMessage({ id: "app.lowCreditsPrompt.title" })}
        </h2>
        <p className="low-credits-prompt-overlay__body">
          {intl.formatMessage(
            { id: "app.lowCreditsPrompt.body" },
            { credits }
          )}
        </p>
        <p className="low-credits-prompt-overlay__hint">
          {intl.formatMessage({ id: "app.lowCreditsPrompt.hint" })}
        </p>
        <div className="low-credits-prompt-overlay__no-again form-check mb-3">
          <input
            id="low-credits-prompt-no-again"
            className="form-check-input"
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => onDontShowAgainChange(e.target.checked)}
          />
          <label
            className="form-check-label"
            htmlFor="low-credits-prompt-no-again"
          >
            {intl.formatMessage({ id: "app.lowCreditsPrompt.noAgain" })}
          </label>
        </div>
        <div className="low-credits-prompt-overlay__actions">
          <Button color="link" className="text-muted p-0" onClick={onLater} type="button">
            {intl.formatMessage({ id: "app.lowCreditsPrompt.later" })}
          </Button>
          <Button color="primary" onClick={onBuyNow} type="button">
            {intl.formatMessage({ id: "app.lowCreditsPrompt.buyNow" })}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
